
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import psl from 'psl';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Stack, 
  Paper, 
  CircularProgress, 
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Checkbox,
  ListItemText,
  ListSubheader,
  Chip,
  Slider,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DevicesIcon from '@mui/icons-material/Devices';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NetworkGraph, { NetworkGraphHandle } from '../components/analysis/NetworkGraph';
import EgoRadialGraph from '../components/analysis/EgoRadialGraph';
import { AreaBounds } from '../components/analysis/NetworkGraphMap';
import LinkAnalysisSentinel, { GraphAction } from '../components/analysis/LinkAnalysisSentinel';
import { SimilarEntitiesPanel } from '@signals/pol-ui';
import NoteDialog from '@signals/app-shell/components/common/NoteDialog';
import EntityDetailsDialog from '@signals/app-shell/components/common/EntityDetailsDialog';
import deviceDataService from '@signals/app-shell/services/deviceDataService';
import polService from '@signals/app-shell/services/polService';
import { useSettingsStore } from '@signals/app-shell/stores/settingsStore';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import { EntityType, Note, getNotesForEntity } from '@signals/app-shell/services/notesApi';
import { generateEntityReportPDF, EntityReportData } from '@signals/app-shell/components/pdf';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';
import {
  buildQueryKey,
  useLinkAnalysisStore,
  type GraphTab,
} from '../stores/linkAnalysisStore';
import { mergeExpandedGraphData } from '../utils/graphExpansion';

/** Bridge Zustand string[] storage to Set-based APIs used by graph components. */
function useSetFieldAdapter(
  ids: string[],
  setIds: (value: string[] | ((prev: string[]) => string[])) => void
): [Set<string>, (value: Set<string> | ((prev: Set<string>) => Set<string>)) => void] {
  const set = useMemo(() => new Set(ids), [ids]);
  const setSet = useCallback(
    (value: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setIds((prev) => {
        const prevSet = new Set(prev);
        const next = typeof value === 'function' ? value(prevSet) : value;
        return Array.from(next);
      });
    },
    [setIds]
  );
  return [set, setSet];
}

function useNullableSetFieldAdapter(
  keys: string[] | null,
  setKeys: (value: string[] | null) => void
): [Set<string> | null, (value: Set<string> | null) => void] {
  const set = useMemo(() => (keys === null ? null : new Set(keys)), [keys]);
  const setSet = useCallback(
    (value: Set<string> | null) => {
      setKeys(value === null ? null : Array.from(value));
    },
    [setKeys]
  );
  return [set, setSet];
}

/**
 * API uses `type: "location"` for some places and `type: "dwell"` for dwell-derived
 * place nodes. Location/category filters must include both.
 */
function isLinkAnalysisPlaceNode(node: { type?: string } | null | undefined): boolean {
  return node?.type === 'location' || node?.type === 'dwell';
}

const GRAPH_BUILD_STEPS = [
  'Matching coverage...',
  'Reading communications...',
  'Linking entities...',
  'Laying out connections...',
];

const BASE_CONNECTION_TYPES = [
  { value: 'digital', label: 'All Digital POL' },
  { value: 'all', label: 'All Types (incl. Dwells)' },
  { value: 'call', label: 'Call Logs' },
  { value: 'sms', label: 'SMS' },
  { value: 'browsing', label: 'Browsing History' },
  { value: 'app', label: 'App Usage' },
  { value: 'dwell', label: 'Dwells Only' },
];

const RF_CONNECTION_TYPES = [
  { value: 'all', label: 'All Types', group: '' },
  { value: 'rf', label: 'RF Signals (WiFi+BLE+Cell)', group: 'RF' },
  { value: 'digital', label: 'All Digital POL', group: 'Digital' },
  { value: 'call', label: 'Call Logs', group: 'Digital' },
  { value: 'sms', label: 'SMS', group: 'Digital' },
  { value: 'browsing', label: 'Browsing History', group: 'Digital' },
  { value: 'app', label: 'App Usage', group: 'Digital' },
  { value: 'dwell', label: 'Dwells Only', group: 'Location' },
];

const LinkAnalysisPage: React.FC = () => {
  // Get user settings from Zustand store
  const { settings } = useSettingsStore();
  const linkAnalysisSettings = settings.visualization.linkAnalysis;
  const rfEnabled = useFeatureFlagsStore((s) => s.rfDashboard);
  const CONNECTION_TYPES = rfEnabled ? RF_CONNECTION_TYPES : BASE_CONNECTION_TYPES;

  // Session state survives navigation between app tabs (COP, Pathfinder, etc.)
  const {
    hasData,
    loadedQueryKey,
    entityTypesInitialized,
    connectionType,
    dateRange,
    selectedEntityIds,
    selectedEntityTypes,
    selectedLocations,
    selectedLocationCategories,
    expandedNodeIds,
    hiddenNodeIds,
    connectionRange,
    suggestedRange,
    maxConnectionsInData,
    selectedDateKeys,
    selectedHours,
    selectedConnectionTypeKeys,
    selectedNodeTypeKeys,
    selectedArea,
    isSimulationFrozen,
    showOnlyKnownDevices,
    showSentinelPanel,
    showRightPanel,
    depthLevel,
    graphViewMode,
    showSimilarPanel,
    similarPanelEntity,
    selectedCourse,
    aiHighlightedNodeIds,
    tabs,
    activeTab,
    selectionModeEnabled,
    setEntityTypesInitialized,
    setConnectionType,
    setDateRange,
    setSelectedEntityIds,
    setSelectedEntityTypes,
    setSelectedLocations,
    setSelectedLocationCategories,
    setExpandedNodeIds,
    setHiddenNodeIds,
    setConnectionRange,
    setSuggestedRange,
    setMaxConnectionsInData,
    setSelectedDateKeys,
    setSelectedHours,
    setSelectedConnectionTypeKeys,
    setSelectedNodeTypeKeys,
    setSelectedArea,
    setIsSimulationFrozen,
    setShowOnlyKnownDevices,
    setShowSentinelPanel,
    setShowRightPanel,
    setDepthLevel,
    setGraphViewMode,
    setShowSimilarPanel,
    setSimilarPanelEntity,
    setSelectedCourse,
    setAiHighlightedNodeIds,
    setTabs,
    setActiveTab,
    setSelectionModeEnabled,
    markDataLoaded,
  } = useLinkAnalysisStore();

  const [expandedNodes, setExpandedNodes] = useSetFieldAdapter(expandedNodeIds, setExpandedNodeIds);
  const [hiddenNodes, setHiddenNodes] = useSetFieldAdapter(hiddenNodeIds, setHiddenNodeIds);
  const [selectedDates, setSelectedDates] = useNullableSetFieldAdapter(selectedDateKeys, setSelectedDateKeys);
  const [selectedConnectionTypes, setSelectedConnectionTypes] = useNullableSetFieldAdapter(
    selectedConnectionTypeKeys,
    setSelectedConnectionTypeKeys
  );
  const [selectedNodeTypes, setSelectedNodeTypes] = useNullableSetFieldAdapter(
    selectedNodeTypeKeys,
    setSelectedNodeTypeKeys
  );
  const [aiHighlightedNodes, setAiHighlightedNodes] = useSetFieldAdapter(
    aiHighlightedNodeIds,
    setAiHighlightedNodeIds
  );

  const [availableCourses, setAvailableCourses] = useState<{id: string; name: string}[]>([]);
  
  // Note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogTarget, setNoteDialogTarget] = useState<{
    entityType: EntityType;
    entityId: string;
    entityLabel?: string;
    latitude?: number;
    longitude?: number;
  } | null>(null);
  
  // Entity details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsDialogTarget, setDetailsDialogTarget] = useState<{
    entityType: EntityType;
    entityId: string;
    entityLabel?: string;
    stats?: {
      totalConnections: number;
      callConnections: number;
      smsConnections: number;
      browsingConnections: number;
      appConnections: number;
      dwellConnections: number;
      connectedDevices: string[];
      connectedLocations: string[];
    };
    websiteDetails?: {
      domain: string;
      currentHost?: string;
      sampleUrl?: string;
      hosts: string[];
      hasOtherSubdomainsInView?: boolean;
    };
  } | null>(null);
  
  // Ref for the network graph component
  const graphRef = React.useRef<NetworkGraphHandle>(null);
  
  const [selectedNodesForExport, setSelectedNodesForExport] = useState<any[]>([]);
  
  // Graph's internal filtered state (after selection/fan-out filtering)
  const [graphFilteredNodes, setGraphFilteredNodes] = useState<any[]>([]);
  const [graphFilteredLinks, setGraphFilteredLinks] = useState<any[]>([]);
  const [graphSelectedCount, setGraphSelectedCount] = useState(0);
  
  // Stable callback for graph to report filtered data (prevents infinite re-renders)
  const handleGraphFilteredDataChange = useCallback((nodes: any[], links: any[], selectedCount: number) => {
    setGraphFilteredNodes(nodes);
    setGraphFilteredLinks(links);
    setGraphSelectedCount(selectedCount);
  }, []);
  
  // Data state
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState<string | null>(null);
  const [buildStep, setBuildStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [_selectedNode, setSelectedNode] = useState<any>(null);
  
  // Dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTabId, setRenameTabId] = useState<string>('');
  const [renameTabLabel, setRenameTabLabel] = useState('');
  
  // Filter popup dialog state
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  
  // Local state for Entity Filters dialog (only applied on "Apply")
  const [localEntityTypes, setLocalEntityTypes] = useState<string[]>([]);
  const [localEntityIds, setLocalEntityIds] = useState<string[]>([]);
  
  // Local state for Connection & Location Filters dialog (only applied on "Apply")
  const [localConnectionType, setLocalConnectionType] = useState<string>('all');
  const [localLocations, setLocalLocations] = useState<string[]>([]);
  const [localCategories, setLocalCategories] = useState<string[]>([]);
  const [locationSearch, setLocationSearch] = useState<string>('');
  const [categorySearch, setCategorySearch] = useState<string>('');
  const [entitySearch, setEntitySearch] = useState<string>('');

  // When the user changes connection type AND picks locations/categories in the
  // same Apply click, the location/category values can't be validated yet because
  // the new data hasn't loaded. We stash them here so the fetch effect can apply
  // them after the data arrives.
  const pendingLocFiltersRef = useRef<{ locations: string[]; categories: string[] } | null>(null);  
  // Local state for Date Range dialog (only applied on "Apply")
  const [localDateRange, setLocalDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  
  // Get current tab data
  const currentTab = tabs.find(tab => tab.id === activeTab) || tabs[0];
  
  // Get original tab data (for expansions - always look up from full dataset)
  const originalTab = tabs.find(tab => tab.isOriginal) || tabs[0];

  // Helper function to count connections per node from a set of links
  const countConnections = (links: any[]) => {
    const connectionCount = new Map<string, number>();
    links.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      connectionCount.set(sourceId, (connectionCount.get(sourceId) || 0) + 1);
      connectionCount.set(targetId, (connectionCount.get(targetId) || 0) + 1);
    });
    return connectionCount;
  };

  // Helper to check if a link is within the selected area
  const isLinkInArea = (link: any) => {
    if (!selectedArea) return true;
    const lat = link.latitude;
    const lng = link.longitude;
    if (lat === undefined || lng === undefined) return false; // No location data
    return lat >= selectedArea.minLat && lat <= selectedArea.maxLat &&
           lng >= selectedArea.minLng && lng <= selectedArea.maxLng;
  };

  // BASE FILTERED DATA: Apply connection range + area filter FIRST
  // This determines what options are available in the dropdowns
  const baseFilteredData = useMemo(() => {
    const hasConnectionRangeFilter = connectionRange[0] > 1 || connectionRange[1] < maxConnectionsInData;
    const hasAreaFilter = selectedArea !== null;
    const hasDateFilter = selectedDates !== null && selectedDates.size > 0;
    const hasHourFilter = selectedHours !== null && selectedHours.length > 0;
    const hasConnTypeFilter = selectedConnectionTypes !== null && selectedConnectionTypes.size > 0;
    const hasNodeTypeFilter = selectedNodeTypes !== null && selectedNodeTypes.size > 0;
    
    // Helper to get LOCAL date key (YYYY-MM-DD) to avoid timezone issues
    const getLocalDateKey = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Helper to check if link matches date filter
    const linkMatchesDateFilter = (link: any) => {
      if (!hasDateFilter || !link.timestamp) return true;
      const parsed = parseBackendDateTime(link.timestamp);
      if (!parsed) return true;
      const dateKey = getLocalDateKey(parsed);
      return selectedDates!.has(dateKey);
    };
    
    // Helper to check if link matches hour filter
    const linkMatchesHourFilter = (link: any) => {
      if (!hasHourFilter || !link.timestamp) return true;
      const date = parseBackendDateTime(link.timestamp);
      if (!date) return true;
      const day = date.getDay();
      const hour = date.getHours();
      return selectedHours!.some(h => h.day === day && h.hour === hour);
    };
    
    // Helper to check if link matches connection type filter
    const linkMatchesConnTypeFilter = (link: any) => {
      if (!hasConnTypeFilter) return true;
      return selectedConnectionTypes!.has(link.type);
    };
    
    if (!hasConnectionRangeFilter && !hasAreaFilter && !hasDateFilter && !hasHourFilter && !hasConnTypeFilter && !hasNodeTypeFilter) {
      // Still normalize links to ensure source/target are string IDs (D3 mutates them)
      const normalizedLinks = currentTab.links.map((link: any) => ({
        ...link,
        source: typeof link.source === 'string' ? link.source : link.source?.id,
        target: typeof link.target === 'string' ? link.target : link.target?.id,
      }));
      return { nodes: currentTab.nodes, links: normalizedLinks };
    }
    
    // First apply area filter to links if active
    let linksToProcess = currentTab.links;
    if (hasAreaFilter) {
      linksToProcess = linksToProcess.filter(isLinkInArea);
    }
    
    // Apply date filter
    if (hasDateFilter) {
      linksToProcess = linksToProcess.filter(linkMatchesDateFilter);
    }
    
    // Apply hour filter
    if (hasHourFilter) {
      linksToProcess = linksToProcess.filter(linkMatchesHourFilter);
    }
    
    // Apply connection type filter
    if (hasConnTypeFilter) {
      linksToProcess = linksToProcess.filter(linkMatchesConnTypeFilter);
    }
    
    // Apply connection range filter
    const connectionCount = countConnections(linksToProcess);
    const nodesInRange = new Set<string>();
    
    // Apply node type filter to nodes
    let nodesToProcess = currentTab.nodes;
    if (hasNodeTypeFilter) {
      nodesToProcess = currentTab.nodes.filter((node: any) => selectedNodeTypes!.has(node.type));
    }
    
    nodesToProcess.forEach((node: any) => {
      const count = connectionCount.get(node.id) || 0;
      if (count >= connectionRange[0] && count <= connectionRange[1]) {
        nodesInRange.add(node.id);
      }
    });
    
    const filteredNodes = nodesToProcess.filter((node: any) => nodesInRange.has(node.id));
    // Normalize links to ensure source/target are always string IDs
    const filteredLinks = linksToProcess.filter((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      return nodesInRange.has(sourceId) && nodesInRange.has(targetId);
    }).map((link: any) => ({
      ...link,
      source: typeof link.source === 'string' ? link.source : link.source?.id,
      target: typeof link.target === 'string' ? link.target : link.target?.id,
    }));
    
    return { nodes: filteredNodes, links: filteredLinks };
  }, [currentTab.nodes, currentTab.links, connectionRange, maxConnectionsInData, selectedArea, selectedDates, selectedHours, selectedConnectionTypes, selectedNodeTypes]);

  // Extract unique entity types from BASE FILTERED nodes (updates as slider/area changes)
  // Uses label prefix (e.g., "Trainee" from "Trainee-02") to distinguish subtypes
  const availableEntityTypes = useMemo(() => {
    const entityTypes = new Set<string>();
    baseFilteredData.nodes.forEach((node: any) => {
      if (node.type === 'device') {
        // Extract prefix from label (e.g., "Trainee" from "Trainee-02", "Student" from "Student-25")
        const label = node.label || node.id || '';
        const match = label.match(/^([A-Za-z]+)[-_]?\d/);
        if (match) {
          entityTypes.add(match[1]);
        } else if (node.entity_type) {
          // Fallback to entity_type if no prefix pattern found
          entityTypes.add(node.entity_type);
        }
      }
    });
    return Array.from(entityTypes).sort();
  }, [baseFilteredData.nodes]);

  // selectedEntityTypes defaults to "all types" — we sync it with availableEntityTypes
  // only on the FIRST data load (not on every change, to avoid infinite loops).
  useEffect(() => {
    if (!entityTypesInitialized && availableEntityTypes.length > 0) {
      setEntityTypesInitialized(true);
      setSelectedEntityTypes(availableEntityTypes);
    }
  }, [availableEntityTypes, entityTypesInitialized, setEntityTypesInitialized, setSelectedEntityTypes]);

  // Extract unique entity IDs from BASE FILTERED nodes
  const availableEntityIds = useMemo(() => {
    const entityIds = new Set<string>();
    baseFilteredData.nodes.forEach((node: any) => {
      if (node.type === 'device' && node.id && !node.id.startsWith('phone:')) {
        entityIds.add(node.id);
      }
    });
    return Array.from(entityIds).sort();
  }, [baseFilteredData.nodes]);

  // Map entity ID -> entity-type prefix (same extraction rule as availableEntityTypes).
  // Used to cascade the "Filter by Entity" dropdown based on the selected Entity Type(s).
  const deviceEntityTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    baseFilteredData.nodes.forEach((node: any) => {
      if (node.type !== 'device' || !node.id || node.id.startsWith('phone:')) return;
      const label = node.label || node.id || '';
      const match = label.match(/^([A-Za-z]+)[-_]?\d/);
      if (match) {
        map.set(node.id, match[1]);
      } else if (node.entity_type) {
        map.set(node.id, node.entity_type);
      }
    });
    return map;
  }, [baseFilteredData.nodes]);

  // Extract unique locations from BASE FILTERED nodes
  const availableLocations = useMemo(() => {
    const locationsByLabel = new Map<string, string[]>();
    baseFilteredData.nodes.forEach((node: any) => {
      if (isLinkAnalysisPlaceNode(node) && node.id) {
        const label = node.place_name || node.label || node.id;
        if (!locationsByLabel.has(label)) {
          locationsByLabel.set(label, []);
        }
        locationsByLabel.get(label)!.push(node.id);
      }
    });
    return Array.from(locationsByLabel.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, ids]) => ({ id: ids.join(','), label, ids }));
  }, [baseFilteredData.nodes]);

  // Extract unique location categories from BASE FILTERED nodes
  const availableLocationCategories = useMemo(() => {
    const categories = new Set<string>();
    baseFilteredData.nodes.forEach((node: any) => {
      if (isLinkAnalysisPlaceNode(node) && node.place_type) {
        categories.add(node.place_type);
      }
    });
    // Dwell edges sometimes carry place_type even when a merged node missed it
    baseFilteredData.links.forEach((link: any) => {
      if (link.type === 'dwell' && link.place_type) {
        categories.add(link.place_type);
      }
    });
    return Array.from(categories).sort();
  }, [baseFilteredData.nodes, baseFilteredData.links]);

  // FINAL FILTERED DATA: Apply entity/location filters ON TOP OF base filtered data
  const filteredData = useMemo(() => {
    // Only consider selections that are still valid (exist in available options)
    const validEntityTypes = selectedEntityTypes.filter(t => availableEntityTypes.includes(t));
    const availableLocationIds = new Set(availableLocations.map(l => l.id));
    const validLocations = selectedLocations.filter(id => availableLocationIds.has(id));
    const validCategories = selectedLocationCategories.filter(c => availableLocationCategories.includes(c));
    
    const hasEntityFilter = selectedEntityIds.length > 0;
    const hasEntityTypeFilter = validEntityTypes.length > 0 && validEntityTypes.length < availableEntityTypes.length;
    const hasLocationFilter = validLocations.length > 0;
    const hasCategoryFilter = validCategories.length > 0;
    const hasAnyEntityFilter = hasEntityFilter || hasEntityTypeFilter || hasLocationFilter || hasCategoryFilter;
    
    // If no entity/location filters, start from base filtered data (expansion merged below)
    if (!hasAnyEntityFilter) {
      return mergeExpandedGraphData(
        baseFilteredData,
        originalTab.nodes,
        originalTab.links,
        expandedNodes,
        hiddenNodes
      );
    }

    // Build set of valid location IDs based on filters
    const filterLocationIds = new Set<string>();
    
    // Add locations matching selected categories (from base filtered nodes)
    if (hasCategoryFilter) {
      baseFilteredData.nodes.forEach((node: any) => {
        if (isLinkAnalysisPlaceNode(node) && node.place_type && validCategories.includes(node.place_type)) {
          filterLocationIds.add(node.id);
        }
      });
      baseFilteredData.links.forEach((link: any) => {
        if (link.type !== 'dwell' || !link.place_type) return;
        if (!validCategories.includes(link.place_type)) return;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        if (targetId) {
          filterLocationIds.add(targetId);
        }
      });
    }

    // Add specific selected locations (each selection may map to multiple node IDs)
    if (hasLocationFilter) {
      validLocations.forEach(idString => {
        idString.split(',').forEach(id => filterLocationIds.add(id));
      });
    }

    // Track nodes that should be included
    const connectedNodeIds = new Set<string>();
    
    // Track links to include (using a Map to dedupe by link identity)
    const linksToInclude = new Map<string, any>();
    
    // Helper to get link key for deduplication
    const getLinkKey = (link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      return `${sourceId}-${targetId}-${link.connection_type || ''}-${link.timestamp || ''}`;
    };
    
    // Add all selected entities
    if (hasEntityFilter) {
      selectedEntityIds.forEach(id => connectedNodeIds.add(id));
    }

    // Build a node lookup map for faster access (from ORIGINAL data for expanded nodes)
    const allNodeMap = new Map<string, any>();
    originalTab.nodes.forEach((node: any) => allNodeMap.set(node.id, node));
    // Also add current tab nodes (for subset-specific nodes if any)
    currentTab.nodes.forEach((node: any) => allNodeMap.set(node.id, node));
    
    // Process base filtered links for regular filter logic
    baseFilteredData.links.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;

      // Skip if already included via expansion
      if (linksToInclude.has(getLinkKey(link))) {
        return;
      }

      // Apply filters
      let includeLink = false;

      // Entity filter check (specific entities)
      if (hasEntityFilter) {
        const hasSelectedEntity = selectedEntityIds.includes(sourceId) || selectedEntityIds.includes(targetId);
        if (!hasSelectedEntity) {
          return;
        }
        includeLink = true;
      }

      // Entity type filter check (uses label prefix, e.g., "Trainee", "Student")
      if (hasEntityTypeFilter && !hasEntityFilter) {
        const sourceNode = allNodeMap.get(sourceId);
        const targetNode = allNodeMap.get(targetId);
        
        // Helper to check if node matches any selected entity type by label prefix
        const nodeMatchesType = (node: any) => {
          if (!node || node.type !== 'device') return false;
          const label = node.label || node.id || '';
          const match = label.match(/^([A-Za-z]+)[-_]?\d/);
          const prefix = match ? match[1] : node.entity_type;
          return prefix && validEntityTypes.includes(prefix);
        };
        
        const sourceMatches = nodeMatchesType(sourceNode);
        const targetMatches = nodeMatchesType(targetNode);
        
        if (!sourceMatches && !targetMatches) {
          return;
        }
        includeLink = true;
      }

      // Location/Category filter check
      if (hasLocationFilter || hasCategoryFilter) {
        const sourceNode = allNodeMap.get(sourceId);
        const targetNode = allNodeMap.get(targetId);
        
        const sourceIsLocation = isLinkAnalysisPlaceNode(sourceNode);
        const targetIsLocation = isLinkAnalysisPlaceNode(targetNode);

        let hasValidLocation = false;
        if (sourceIsLocation && filterLocationIds.has(sourceId)) {
          hasValidLocation = true;
        }
        if (targetIsLocation && filterLocationIds.has(targetId)) {
          hasValidLocation = true;
        }

        if (!hasValidLocation) {
          return;
        }
        includeLink = true;
      }

      if (includeLink) {
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
        linksToInclude.set(getLinkKey(link), link);
      }
    });

    // Get nodes - combine from current tab AND original data (for expanded node connections)
    // Use allNodeMap which has both original and current tab nodes
    const filteredNodes: any[] = [];
    const addedNodeIds = new Set<string>();
    
    connectedNodeIds.forEach(nodeId => {
      if (!addedNodeIds.has(nodeId)) {
        const node = allNodeMap.get(nodeId);
        if (node) {
          filteredNodes.push(node);
          addedNodeIds.add(nodeId);
        }
      }
    });

    // Normalize links to ensure source/target are always string IDs
    // (D3's forceLink mutates links to use object references, which breaks when data changes)
    // Normalize links to ensure source/target are always string IDs
    const normalizedLinks = Array.from(linksToInclude.values()).map((link: any) => ({
      ...link,
      source: typeof link.source === 'string' ? link.source : link.source?.id,
      target: typeof link.target === 'string' ? link.target : link.target?.id,
    }));

    const entityFiltered = { nodes: filteredNodes, links: normalizedLinks };
    return mergeExpandedGraphData(
      entityFiltered,
      originalTab.nodes,
      originalTab.links,
      expandedNodes,
      hiddenNodes
    );
  }, [currentTab.nodes, currentTab.links, originalTab.nodes, originalTab.links, baseFilteredData, selectedEntityIds, selectedEntityTypes, availableEntityTypes, selectedLocations, availableLocations, selectedLocationCategories, availableLocationCategories, expandedNodes, hiddenNodes]);

  // Calculate node depths via BFS from selected entities using BASE data (not filtered)
  // This allows depth filtering to expand beyond what the entity filter would normally show
  const depthData = useMemo(() => {
    // Only calculate if we have selected entities
    if (selectedEntityIds.length === 0) {
      return { nodeDepths: new Map<string, number>(), depthCounts: [0, 0, 0, 0, 0], nodesAtDepth: new Map<number, Set<string>>() };
    }

    const nodeDepths = new Map<string, number>();
    const nodesAtDepth = new Map<number, Set<string>>();
    nodesAtDepth.set(0, new Set<string>());
    nodesAtDepth.set(1, new Set<string>());
    nodesAtDepth.set(2, new Set<string>());
    nodesAtDepth.set(3, new Set<string>());
    nodesAtDepth.set(4, new Set<string>());
    const visited = new Set<string>();
    const queue: Array<{ nodeId: string; depth: number }> = [];

    // Build adjacency list from BASE FILTERED data (before entity filtering)
    // This includes all connections in the current date range / connection type
    const adjacency = new Map<string, Set<string>>();
    baseFilteredData.links.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      if (!sourceId || !targetId) return;
      
      if (!adjacency.has(sourceId)) adjacency.set(sourceId, new Set());
      if (!adjacency.has(targetId)) adjacency.set(targetId, new Set());
      adjacency.get(sourceId)!.add(targetId);
      adjacency.get(targetId)!.add(sourceId);
    });

    // Initialize BFS from selected entities (they are depth 0)
    selectedEntityIds.forEach(id => {
      if (!visited.has(id)) {
        visited.add(id);
        nodeDepths.set(id, 0);
        nodesAtDepth.get(0)!.add(id);
        queue.push({ nodeId: id, depth: 0 });
      }
    });

    // BFS to assign depths
    while (queue.length > 0) {
      const { nodeId, depth } = queue.shift()!;
      const neighbors = adjacency.get(nodeId);
      if (!neighbors) continue;

      neighbors.forEach(neighborId => {
        if (!visited.has(neighborId)) {
          visited.add(neighborId);
          const neighborDepth = depth + 1;
          nodeDepths.set(neighborId, neighborDepth);
          if (neighborDepth <= 4) {
            nodesAtDepth.get(neighborDepth)!.add(neighborId);
          }
          // Continue BFS up to depth 4 (we don't need to explore further)
          if (neighborDepth < 4) {
            queue.push({ nodeId: neighborId, depth: neighborDepth });
          }
        }
      });
    }

    // Count nodes at each depth level (0 = selected, 1-4 = hop distance)
    const depthCounts = [0, 0, 0, 0, 0]; // depths 0, 1, 2, 3, 4
    nodeDepths.forEach(d => {
      if (d >= 0 && d <= 4) depthCounts[d]++;
    });

    return { nodeDepths, depthCounts, nodesAtDepth };
  }, [baseFilteredData.links, selectedEntityIds]);

  // Apply depth filtering to get final display data
  const depthFilteredData = useMemo(() => {
    // If no selected entities, return original filteredData
    if (selectedEntityIds.length === 0) {
      return filteredData;
    }

    const { nodeDepths } = depthData;
    
    // Determine max depth to show: 'all' means show all levels (up to 4)
    const maxDepthToShow = depthLevel === 'all' ? 4 : depthLevel;

    // When depth filtering is active, we want to show ALL nodes within that depth
    // from the BASE data (not the entity-filtered data which only has direct connections)
    
    // Build a node lookup map from ORIGINAL data (for expanded nodes)
    const allNodeMap = new Map<string, any>();
    originalTab.nodes.forEach((node: any) => allNodeMap.set(node.id, node));
    baseFilteredData.nodes.forEach((node: any) => allNodeMap.set(node.id, node));

    // Get all nodes within the selected depth
    const includedNodeIds = new Set<string>();
    nodeDepths.forEach((depth, nodeId) => {
      if (depth <= maxDepthToShow) {
        includedNodeIds.add(nodeId);
      }
    });

    const linksToInclude = new Map<string, any>();

    // Get nodes from the node map
    const depthFilteredNodes = Array.from(includedNodeIds)
      .map(id => allNodeMap.get(id))
      .filter(Boolean);

    // Filter links from base data to only include those where both ends are in filtered nodes
    baseFilteredData.links.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      if (includedNodeIds.has(sourceId) && includedNodeIds.has(targetId)) {
        const linkKey = `${sourceId}-${targetId}-${link.connection_type || ''}-${link.timestamp || ''}`;
        if (!linksToInclude.has(linkKey)) {
          linksToInclude.set(linkKey, {
            ...link,
            source: sourceId,
            target: targetId,
          });
        }
      }
    });

    const depthFilteredLinks = Array.from(linksToInclude.values());

    let depthBase = { nodes: depthFilteredNodes, links: depthFilteredLinks };

    // When entity/depth is active, this path normally ignores filteredData, so
    // location + category (Connection & Location modal) had no effect. Intersect
    // with full filteredData for link identity when those filters are on.
    const hasLocOrCat =
      (selectedLocations.length > 0 || selectedLocationCategories.length > 0) &&
      selectedEntityIds.length > 0;
    if (hasLocOrCat) {
      const linkKeyFn = (link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        return `${sourceId}-${targetId}-${link.type || ''}-${link.timestamp || ''}`;
      };
      const allowed = new Set(filteredData.links.map((l: any) => linkKeyFn(l)));
      const filteredLinks = depthBase.links.filter((l: any) => allowed.has(linkKeyFn(l)));
      const keep = new Set<string>();
      filteredLinks.forEach((l: any) => {
        keep.add(l.source);
        keep.add(l.target);
      });
      depthBase = {
        nodes: depthBase.nodes.filter((n: any) => keep.has(n.id)),
        links: filteredLinks,
      };
    }

    let merged = mergeExpandedGraphData(
      depthBase,
      originalTab.nodes,
      originalTab.links,
      expandedNodes,
      hiddenNodes
    );

    if (showOnlyKnownDevices) {
      merged = {
        nodes: merged.nodes.filter((node: any) => {
          const id = node.id;
          return !(typeof id === 'string' && id.startsWith('phone:'));
        }),
        links: merged.links,
      };
      const visibleNodeIds = new Set(merged.nodes.map((n: any) => n.id));
      merged = {
        ...merged,
        links: merged.links.filter(
          (link: any) => visibleNodeIds.has(link.source) && visibleNodeIds.has(link.target)
        ),
      };
    }

    return merged;
  }, [filteredData, baseFilteredData, originalTab, depthLevel, depthData, selectedEntityIds, selectedLocations, selectedLocationCategories, expandedNodes, hiddenNodes, showOnlyKnownDevices]);

  const graphDisplayData = useMemo(() => {
    if (graphViewMode === 'radial' && selectedEntityIds.length > 0) {
      return depthFilteredData;
    }
    return filteredData;
  }, [graphViewMode, selectedEntityIds.length, depthFilteredData, filteredData]);

  // Reset depth filter when no entities are selected
  useEffect(() => {
    if (selectedEntityIds.length === 0 && depthLevel !== 'all') {
      setDepthLevel('all');
    }
  }, [selectedEntityIds.length, depthLevel]);

  useEffect(() => {
    if (!loading) return;
    const timer = window.setInterval(() => {
      setBuildStep((step) => (step + 1) % GRAPH_BUILD_STEPS.length);
    }, 2200);
    return () => window.clearInterval(timer);
  }, [loading]);

  // Fetch connection data when filters change — skip if returning with cached session data
  const prevConnectionTypeRef = useRef(connectionType);
  const prevDateRangeRef = useRef(dateRange);
  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    const queryKey = buildQueryKey(connectionType, dateRange);
    if (!queryKey) {
      return;
    }
    const rangeStart = dateRange.start;
    const rangeEnd = dateRange.end;
    if (!rangeStart || !rangeEnd) {
      return;
    }
    if (hasData && loadedQueryKey === queryKey) {
      return;
    }

    const fetchConnectionData = async () => {
      setLoading(true);
      setError(null);
      setLoadNotice(null);
      setBuildStep(0);
      
      const dateChanged =
        prevDateRangeRef.current.start?.getTime() !== dateRange.start?.getTime() ||
        prevDateRangeRef.current.end?.getTime() !== dateRange.end?.getTime();
      if (dateChanged) {
        setSelectedLocations([]);
        setSelectedLocationCategories([]);
      }
      setExpandedNodes(new Set());
      setHiddenNodes(new Set());

      prevConnectionTypeRef.current = connectionType;
      prevDateRangeRef.current = dateRange;
      setEntityTypesInitialized(false);
      
      try {
        const startDateStr = rangeStart.toISOString().split('T')[0];
        const endDateStr = rangeEnd.toISOString().split('T')[0];
        
        const response = await deviceDataService.getLinkAnalysisConnections(
          connectionType,
          startDateStr,
          endDateStr,
          undefined,
          undefined,
          { signal: abortController.signal, timeoutMs: 150000 }
        );

        // Stale response guard — if connectionType or dateRange changed while
        // this request was in flight, discard the result.
        if (cancelled) return;

        let newLinks = (response.edges || []).filter((e: any) => e && (e.source != null) && (e.target != null));
        // RF mode is signal-based only; drop GPS dwell edges if backend still returns them (legacy/cached).
        if (connectionType === 'rf') {
          newLinks = newLinks.filter((e: any) => e.type !== 'dwell');
        }
        const connectedIds = new Set<string>();
        newLinks.forEach((link: any) => {
          const s = typeof link.source === 'string' ? link.source : link.source?.id;
          const t = typeof link.target === 'string' ? link.target : link.target?.id;
          if (s) connectedIds.add(s);
          if (t) connectedIds.add(t);
        });
        const newNodes = (response.nodes || []).filter((n: any) => n && n.id && connectedIds.has(n.id));
        
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.isOriginal 
            ? { ...tab, nodes: newNodes, links: newLinks }
            : tab
        ));
        
        const TARGET_NODES = 500;
        const connectionCount = new Map<string, number>();
        newLinks.forEach((link: any) => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
          connectionCount.set(sourceId, (connectionCount.get(sourceId) || 0) + 1);
          connectionCount.set(targetId, (connectionCount.get(targetId) || 0) + 1);
        });
        
        const counts = newNodes.map((n: any) => connectionCount.get(n.id) || 0);
        const maxConnections = Math.max(...counts, 1);
        setMaxConnectionsInData(maxConnections);
        
        if (newNodes.length > TARGET_NODES) {
          const sortedCounts = [...counts].sort((a: number, b: number) => b - a);
          const optimalMin = Math.max(sortedCounts.length > TARGET_NODES ? sortedCounts[TARGET_NODES - 1] : 1, 1);
          
          setSuggestedRange([optimalMin, maxConnections]);
          setConnectionRange([1, maxConnections]);
          console.log(`Connection Range set to ALL (1-${maxConnections}). Suggested filter: ${optimalMin}-${maxConnections} for ~${TARGET_NODES} nodes`);
        } else {
          setSuggestedRange([1, maxConnections]);
          setConnectionRange([1, maxConnections]);
        }

        markDataLoaded(connectionType, dateRange);
        const serverWarnings = Array.isArray(response?.stats?.warnings)
          ? response.stats.warnings.filter((w: unknown) => typeof w === 'string' && w.trim())
          : [];
        setLoadNotice(serverWarnings.length ? serverWarnings.join(' ') : null);
        
      } catch (error: any) {
        if (cancelled || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        console.error('Failed to fetch connection data:', error);
        const timedOut =
          error?.code === 'ECONNABORTED' ||
          String(error?.message || '').toLowerCase().includes('timeout');
        const status = error?.response?.status;
        setLoadNotice(null);
        setError(
          timedOut
            ? 'Link analysis is still building this window. Narrow the dates or connection type and try again.'
            : status === 500
              ? 'Link analysis could not finish this date range. Narrow the dates or connection type and try again.'
              : error.message || 'Failed to load network data'
        );
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.isOriginal 
            ? { ...tab, nodes: [], links: [] }
            : tab
        ));
      } finally {
        if (!cancelled) {
          setLoading(false);
          if (pendingLocFiltersRef.current) {
            const { locations, categories } = pendingLocFiltersRef.current;
            pendingLocFiltersRef.current = null;
            if (locations.length > 0) setSelectedLocations(locations);
            if (categories.length > 0) setSelectedLocationCategories(categories);
          }
        }
      }
    };

    fetchConnectionData();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [
    connectionType,
    dateRange,
    hasData,
    loadedQueryKey,
    markDataLoaded,
    setEntityTypesInitialized,
    setExpandedNodes,
    setHiddenNodes,
    setSelectedLocations,
    setSelectedLocationCategories,
    setTabs,
    setConnectionRange,
    setSuggestedRange,
    setMaxConnectionsInData,
  ]);

  // Fetch available courses for embedding similarity search
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await polService.getCourses();
        if (response.courses && response.courses.length > 0) {
          setAvailableCourses(response.courses.map((c) => ({
            id: c.id,
            name: c.label || c.id,
          })));
          // Auto-select first course if none selected
          if (!selectedCourse) {
            setSelectedCourse(response.courses[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch courses for similarity panel:', error);
      }
    };
    fetchCourses();
  }, []);

  const handleNodeClick = (node: any) => {
    setSelectedNode(node);
    // Note: Node expansion is now handled via right-click context menu only
    // (Expand Node / Collapse Node options) to avoid confusing behavior
    // where every click would toggle expansion state
  };

  const handleClearExpansions = () => {
    setExpandedNodes(new Set());
  };

  // Expand all device/entity nodes
  const handleExpandAll = () => {
    // Get the original tab data (full dataset) to find all expandable nodes
    const originalTab = tabs.find(tab => tab.isOriginal) || tabs[0];
    
    // Only expand device and entity type nodes (not locations, websites, etc.)
    // Use original data so we can expand nodes even when filtered to a single entity
    const expandableNodeIds = originalTab.nodes
      .filter((n: any) => n.type === 'device' || n.type === 'entity' || n.type === 'phone')
      .map((n: any) => n.id);
    setExpandedNodes(new Set(expandableNodeIds));
  };

  // Toggle node expansion (for right-click menu)
  const handleToggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Toggle node hide (for right-click menu - hide nodes that weren't expanded)
  const handleToggleHide = (nodeId: string) => {
    setHiddenNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const handleSelectionChange = (nodes: any[], autoExport: boolean = false) => {
    console.log('[LinkAnalysis] handleSelectionChange called with', nodes.length, 'nodes, autoExport:', autoExport);
    setSelectedNodesForExport(nodes);
    
    // Auto-export if requested (e.g., from box selection)
    if (autoExport && nodes.length > 0) {
      console.log('[LinkAnalysis] Auto-exporting', nodes.length, 'nodes to new tab');
      // Exit selection mode immediately
      setSelectionModeEnabled(false);
      handleExportToNewTab(nodes);
    }
  };

  // Handler for finding similar entities (embedding-based)
  const handleFindSimilar = (nodeId: string, _nodeLabel: string) => {
    setSimilarPanelEntity(nodeId);
    setShowSimilarPanel(true);
  };

  // Handler for viewing entity details
  const handleViewDetails = (node: any, stats: any) => {
    const entityType: EntityType = node.type === 'device' ? 'device' : 
                                   (node.type === 'location' || node.type === 'dwell') ? 'location' : 'entity';

    const nodesInView = (graphFilteredNodes?.length ? graphFilteredNodes : (tabs.find(t => t.id === activeTab)?.nodes ?? [])) as any[];
    
    // Build websiteDetails for website nodes (URLs are fetched from backend by the dialog)
    const websiteDetails = node?.type === 'website'
      ? (() => {
          const currentHost = String(node.domain || node.label || '').toLowerCase().replace(/^www\./, '').trim();
          if (!currentHost) return undefined;

          const parsedCurrent = psl.parse(currentHost);
          const rootDomain = (typeof parsedCurrent === 'object' && 'domain' in parsedCurrent && parsedCurrent.domain)
            ? String(parsedCurrent.domain)
            : currentHost;

          const candidateHosts = nodesInView
            .filter(n => n?.type === 'website')
            .map(n => String(n.domain || n.label || '').toLowerCase().replace(/^www\./, '').trim())
            .filter(Boolean);

          const hostsUnderRoot = Array.from(new Set(
            candidateHosts.filter(h => h === rootDomain || h.endsWith(`.${rootDomain}`))
          )).sort((a, b) => a.localeCompare(b));

          const hasOthers = hostsUnderRoot.some(h => h !== rootDomain && h !== currentHost);

          return {
            domain: rootDomain,
            currentHost,
            sampleUrl: node.sample_url || node.sampleUrl,
            hosts: hostsUnderRoot,
            hasOtherSubdomainsInView: hasOthers,
          };
        })()
      : undefined;

    setDetailsDialogTarget({
      entityType,
      entityId: node.id,
      entityLabel: node.label || node.id,
      stats,
      websiteDetails,
    });
    setDetailsDialogOpen(true);
  };

  // Handler for adding device note
  const handleAddDeviceNote = (deviceId: string, deviceLabel: string) => {
    setNoteDialogTarget({
      entityType: 'device',
      entityId: deviceId,
      entityLabel: deviceLabel,
    });
    setNoteDialogOpen(true);
  };

  // Handler for saving location as geofence
  const handleSaveAsGeofence = (location: { lat: number; lng: number; name: string; placeType?: string }) => {
    // TODO: Implement geofence creation
    console.log('Save as geofence:', location);
  };

  // Handler for adding location note
  const handleAddLocationNote = (location: { lat: number; lng: number; name: string }) => {
    setNoteDialogTarget({
      entityType: 'location',
      entityId: `${location.lat.toFixed(6)},${location.lng.toFixed(6)}`,
      entityLabel: location.name,
      latitude: location.lat,
      longitude: location.lng,
    });
    setNoteDialogOpen(true);
  };

  // Handler for exporting subgraph
  const handleExportSubgraph = (subgraphNodes: any[], subgraphLinks: any[], centerNode: any) => {
    const newTabId = `subset-${Date.now()}`;
    const newTab: GraphTab = {
      id: newTabId,
      label: `${centerNode.label || centerNode.id} Connections`,
      nodes: subgraphNodes,
      links: subgraphLinks,
      isOriginal: false
    };
    
    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTab(newTabId);
  };

  // Handler for exporting entity report to PDF
  const handleExportEntityReport = async (nodeId: string, nodeLabel: string) => {
    try {
      console.log(`[LinkAnalysis] Exporting entity report for ${nodeLabel} (${nodeId})`);
      
      // Find the node
      const node = graphDisplayData.nodes.find((n: any) => n.id === nodeId);
      if (!node) {
        console.error('Node not found:', nodeId);
        return;
      }
      
      // Get connections for this entity
      const connectionCounts: Record<string, { count: number; type: string; label: string; targetType: string }> = {};
      
      graphDisplayData.links.forEach((link: any) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
        
        if (sourceId === nodeId || targetId === nodeId) {
          const otherId = sourceId === nodeId ? targetId : sourceId;
          const otherNode = graphDisplayData.nodes.find((n: any) => n.id === otherId);
          const key = `${otherId}:${link.type || 'unknown'}`;
          
          if (!connectionCounts[key]) {
            connectionCounts[key] = {
              count: 0,
              type: link.type || 'unknown',
              label: otherNode?.label || otherId,
              targetType: otherNode?.type || 'unknown'
            };
          }
          connectionCounts[key].count += link.weight || 1;
        }
      });
      
      // Convert to EntityReportData connections format
      const connections = Object.entries(connectionCounts).map(([key, data]) => {
        const [targetId] = key.split(':');
        return {
          type: data.type,
          targetId,
          targetLabel: data.label,
          count: data.count
        };
      }).sort((a, b) => b.count - a.count);
      
      // Extract SMS messages with actual content
      const smsMessages = graphDisplayData.links
        .filter((link: any) => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
          return (sourceId === nodeId || targetId === nodeId) && 
                 link.type === 'sms' && 
                 link.sms_message;
        })
        .map((link: any) => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
          const otherId = sourceId === nodeId ? targetId : sourceId;
          const otherNode = graphDisplayData.nodes.find((n: any) => n.id === otherId);
          const isOutgoing = sourceId === nodeId;
          
          return {
            id: link.id || String(Math.random()),
            targetId: otherId,
            targetLabel: otherNode?.label || otherId,
            message: link.sms_message,
            timestamp: link.timestamp || link.first_seen,
            direction: isOutgoing ? 'outgoing' : 'incoming' as 'outgoing' | 'incoming',
          };
        })
        .sort((a: any, b: any) => {
          // Sort by timestamp descending (most recent first)
          if (!a.timestamp && !b.timestamp) return 0;
          if (!a.timestamp) return 1;
          if (!b.timestamp) return -1;
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });
      
      // Extract call records
      const callRecords = graphDisplayData.links
        .filter((link: any) => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
          return (sourceId === nodeId || targetId === nodeId) && link.type === 'call';
        })
        .map((link: any) => {
          const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
          const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
          const otherId = sourceId === nodeId ? targetId : sourceId;
          const otherNode = graphDisplayData.nodes.find((n: any) => n.id === otherId);
          const isOutgoing = sourceId === nodeId;
          
          return {
            id: link.id || String(Math.random()),
            targetId: otherId,
            targetLabel: otherNode?.label || otherId,
            duration: link.duration,
            timestamp: link.timestamp || link.first_seen,
            direction: isOutgoing ? 'outgoing' : 'incoming' as 'outgoing' | 'incoming',
          };
        })
        .sort((a: any, b: any) => {
          if (!a.timestamp && !b.timestamp) return 0;
          if (!a.timestamp) return 1;
          if (!b.timestamp) return -1;
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });
      
      // Calculate stats
      const uniqueLocations = connections.filter(c => c.type === 'dwell').length;
      const uniqueWebsites = connections.filter(c => c.type === 'browsing').length;
      const uniqueContacts = connections.filter(c => c.type === 'call' || c.type === 'sms').length;
      
      // Fetch notes for this entity
      let notes: EntityReportData['notes'] = [];
      try {
        const fetchedNotes = await getNotesForEntity('device', nodeId);
        notes = fetchedNotes.map(n => ({
          id: String(n.id || Math.random()),
          title: n.title || '',
          content: n.content,
          priority: n.priority as 'critical' | 'high' | 'normal' | 'low' | undefined,
          created_at: n.created_at,
        }));
      } catch (e) {
        console.warn('Could not fetch notes:', e);
      }
      
      // Prepare report data
      const reportData: EntityReportData = {
        entityId: nodeId,
        entityLabel: nodeLabel,
        entityType: node.type || 'device',
        dateRange: {
          start: dateRange.start || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          end: dateRange.end || new Date(),
        },
        connections,
        notes,
        smsMessages: smsMessages.length > 0 ? smsMessages : undefined,
        callRecords: callRecords.length > 0 ? callRecords : undefined,
        stats: {
          totalConnections: connections.reduce((sum, c) => sum + c.count, 0),
          uniqueContacts,
          uniqueLocations,
          uniqueWebsites,
        },
        classification: 'Controlled Unclassified Information (CUI)',
      };
      
      // Generate PDF (images are captured automatically by generateEntityReportPDF)
      await generateEntityReportPDF(reportData, `Entity_Report_${nodeLabel}.pdf`);
      
      console.log(`[LinkAnalysis] Entity report exported successfully for ${nodeLabel}`);
    } catch (error) {
      console.error('Error exporting entity report:', error);
    }
  };

  // Handler for AI-triggered graph actions
  const handleGraphAction = (action: GraphAction) => {
    console.log('[LinkAnalysisPage] Graph action received:', action);
    
    switch (action.type) {
      case 'highlight_nodes':
        if (action.nodeIds) {
          // Resolve node IDs case-insensitively
          const resolvedHighlightIds = action.nodeIds
            .map(id => graphDisplayData.nodes.find((n: any) => 
              n.id.toLowerCase() === id.toLowerCase() || 
              n.label?.toLowerCase() === id.toLowerCase()
            )?.id)
            .filter((id): id is string => id !== undefined);
          
          if (resolvedHighlightIds.length > 0) {
            setAiHighlightedNodes(new Set(resolvedHighlightIds));
            console.log(`[AI] Highlighted ${resolvedHighlightIds.length} nodes:`, resolvedHighlightIds);
          } else {
            console.warn(`[AI] No matching nodes found for highlight:`, action.nodeIds);
          }
        }
        break;
        
      case 'clear_highlights':
        setAiHighlightedNodes(new Set());
        console.log('[AI] Cleared highlights');
        break;
        
      case 'filter_entities':
        if (action.nodeIds) {
          // Resolve node IDs case-insensitively
          const resolvedFilterIds = action.nodeIds
            .map(id => graphDisplayData.nodes.find((n: any) => 
              n.id.toLowerCase() === id.toLowerCase() || 
              n.label?.toLowerCase() === id.toLowerCase()
            )?.id)
            .filter((id): id is string => id !== undefined);
          
          if (resolvedFilterIds.length > 0) {
            setSelectedEntityIds(resolvedFilterIds);
            setAiHighlightedNodes(new Set(resolvedFilterIds));
            console.log(`[AI] Filtered and highlighted ${resolvedFilterIds.length} entities:`, resolvedFilterIds);
          } else {
            console.warn(`[AI] No matching nodes found for filter:`, action.nodeIds);
          }
        }
        break;
        
      case 'filter_connection_type':
        if (action.connectionType) {
          setConnectionType(action.connectionType);
          console.log(`[AI] Filtered to ${action.connectionType} connections`);
        }
        break;
        
      case 'expand_node':
        if (action.nodeIds && action.nodeIds.length > 0) {
          const nodeId = action.nodeIds[0];
          setExpandedNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(nodeId);
            return newSet;
          });
          console.log(`[AI] Expanded node: ${nodeId}`);
        }
        break;
        
      case 'zoom_to_node':
        if (action.nodeIds && action.nodeIds.length > 0) {
          const nodeId = action.nodeIds[0];
          // Case-insensitive node lookup
          const node = graphDisplayData.nodes.find((n: any) => 
            n.id.toLowerCase() === nodeId.toLowerCase() || 
            n.label?.toLowerCase() === nodeId.toLowerCase()
          );
          if (node) {
            // Highlight the node
            setAiHighlightedNodes(new Set([node.id]));
            // Use the graph ref to zoom to the node
            if (graphRef.current) {
              graphRef.current.zoomToNode(node.id);
            }
            console.log(`[AI] Zoomed to node: ${node.id}`);
          } else {
            console.warn(`[AI] Node not found for zoom: ${nodeId}`);
          }
        }
        break;
        
      case 'show_on_map':
        if (action.nodeIds) {
          // Get location nodes with coordinates
          const locationNodes = graphDisplayData.nodes.filter((n: any) => 
            action.nodeIds?.includes(n.id) && n.latitude && n.longitude
          );
          if (locationNodes.length > 0) {
            // Highlight the location nodes
            setAiHighlightedNodes(new Set(locationNodes.map((n: any) => n.id)));
            console.log(`[AI] Showing ${locationNodes.length} locations on map`);
          }
        }
        break;
        
      case 'reset_view':
        setAiHighlightedNodes(new Set());
        setExpandedNodes(new Set());
        setSelectedEntityIds([]);
        setConnectionType('digital');  // Reset to default (digital POL)
        graphRef.current?.zoomReset();
        console.log('[AI] Reset view');
        break;
        
      case 'change_date_range':
        if (action.dateRange || action.days) {
          let start: Date;
          let end = new Date();
          
          if (action.dateRange) {
            start = action.dateRange.start;
            end = action.dateRange.end;
          } else if (action.days) {
            start = new Date();
            start.setDate(start.getDate() - action.days);
          } else {
            start = new Date();
            start.setDate(start.getDate() - 7);
          }
          
          setDateRange({ start, end });
          console.log(`[AI] Changed date range to: ${start.toLocaleDateString()} - ${end.toLocaleDateString()} (${action.days || 'custom'} days)`);
          
          // Store follow-up action to execute after data loads
          if (action.followUpAction) {
            // Use a small delay to let data load, then execute follow-up
            setTimeout(() => {
              console.log('[AI] Executing follow-up action after date change');
              // Highlight top connected after data refresh
              const topNodes = graphDisplayData.nodes
                .map((n: any) => ({
                  id: n.id,
                  connections: graphDisplayData.links.filter((l: any) => {
                    const sid = typeof l.source === 'string' ? l.source : l.source?.id;
                    const tid = typeof l.target === 'string' ? l.target : l.target?.id;
                    return sid === n.id || tid === n.id;
                  }).length,
                }))
                .sort((a: any, b: any) => b.connections - a.connections)
                .slice(0, 5)
                .map((n: any) => n.id);
              
              if (topNodes.length > 0) {
                setAiHighlightedNodes(new Set(topNodes));
              }
            }, 2000); // Wait for data to load
          }
        }
        break;
        
      case 'toggle_flow_map':
        // Flow map is controlled within NetworkGraphMap - inform user how to enable it
        console.log('[AI] Toggle flow map requested. Flow map is available in the Map tab when filtering to Dwells only.');
        // Set connection type to dwell to enable flow map option
        if (action.enabled) {
          setConnectionType('dwell');
          console.log('[AI] Set filter to Dwells - Flow map toggle is now available in the Map tab');
        }
        break;
        
      case 'switch_tab':
        // Tab switching is controlled within NetworkGraph - log the requested tab
        console.log(`[AI] Switch to tab: ${action.tabName}`);
        // Note: Full implementation would require exposing tab control from NetworkGraph
        break;
        
      case 'select_nodes':
        // Select specific nodes in the graph (for fan-out/shared location analysis)
        if (action.nodeIds) {
          // This would need to be wired to NetworkGraph's selection state
          // For now, highlight them and filter to them
          setAiHighlightedNodes(new Set(action.nodeIds));
          console.log(`[AI] Selected ${action.nodeIds.length} nodes for analysis`);
        }
        break;
        
      default:
        console.log('[AI] Unknown action type:', action.type);
    }
  };

  const handleExportToNewTab = (nodesToExport?: any[]) => {
    console.log('[LinkAnalysis] handleExportToNewTab called, nodesToExport:', nodesToExport?.length);
    const initialNodes = nodesToExport || selectedNodesForExport;
    console.log('[LinkAnalysis] initial nodes to export:', initialNodes.length);
    if (initialNodes.length === 0) {
      console.log('[LinkAnalysis] No nodes to export, returning');
      return;
    }

    // Get IDs of selected nodes
    const selectedIds = new Set<string>(initialNodes.map((n: any) => n.id));
    console.log('[LinkAnalysis] Selected node IDs:', Array.from(selectedIds));
    
    // Find all links connected to selected nodes (at least one endpoint in selection)
    const connectedLinks = currentTab.links.filter((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      return selectedIds.has(sourceId) || selectedIds.has(targetId);
    });
    
    // Collect IDs of nodes connected via these links
    const connectedNodeIds = new Set<string>(selectedIds);
    connectedLinks.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      connectedNodeIds.add(sourceId);
      connectedNodeIds.add(targetId);
    });
    
    // Get all nodes (selected + connected)
    const allNodeMap = new Map<string, any>();
    currentTab.nodes.forEach((n: any) => allNodeMap.set(n.id, n));
    const exportNodes = Array.from(connectedNodeIds)
      .map(id => allNodeMap.get(id))
      .filter(Boolean);
    
    // Normalize links to have string IDs
    const exportLinks = connectedLinks.map((link: any) => ({
      ...link,
      source: typeof link.source === 'string' ? link.source : link.source.id,
      target: typeof link.target === 'string' ? link.target : link.target.id,
    }));
    
    console.log('[LinkAnalysis] Filtered links:', exportLinks.length, 'nodes:', exportNodes.length);

    // Create new tab
    const newTabId = `tab-${Date.now()}`;
    const newTab: GraphTab = {
      id: newTabId,
      label: `Subset ${tabs.length}`,
      nodes: exportNodes,
      links: exportLinks,
      isOriginal: false,
    };

    console.log('[LinkAnalysis] Creating new tab:', newTabId, 'with', exportNodes.length, 'nodes and', exportLinks.length, 'links');
    setTabs([...tabs, newTab]);
    setActiveTab(newTabId);
    setSelectedNodesForExport([]);
    // Ensure selection mode is off (already set in auto-export, but just in case)
    setSelectionModeEnabled(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    setSelectedNodesForExport([]);
    // Exit selection mode when switching tabs
    setSelectionModeEnabled(false);
  };

  const handleCloseTab = (tabId: string) => {
    if (tabs.length <= 1) return; // Don't close last tab
    
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.isOriginal) return; // Don't close original tab
    
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);
    
    // Switch to original tab if closing active tab
    if (activeTab === tabId) {
      setActiveTab(newTabs[0].id);
    }
    
    // Clear graph selection when closing a subset tab
    // This ensures the selection filter is removed from the filter bar
    graphRef.current?.clearSelection();
  };

  const handleDuplicateTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const newTabId = `tab-${Date.now()}`;
    const newTab: GraphTab = {
      id: newTabId,
      label: `${tab.label} (Copy)`,
      nodes: [...tab.nodes],
      links: [...tab.links],
      isOriginal: false,
    };

    setTabs([...tabs, newTab]);
    setActiveTab(newTabId);
  };

  const handleRenameTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    setRenameTabId(tabId);
    setRenameTabLabel(tab.label);
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = () => {
    setTabs(tabs.map(tab => 
      tab.id === renameTabId 
        ? { ...tab, label: renameTabLabel }
        : tab
    ));
    setRenameDialogOpen(false);
  };

  const { courseSingular } = useCourseLabels();

  return (
    <Box 
      id="link-analysis-dashboard"
      sx={{ 
        // Fill the parent <main> container that Layout already sized to
        // (100vh - top/bottom CUI banners). Hard-coding `calc(100vh - 96px)`
        // here under-counted the available height and left a dead band at
        // the bottom of the page.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 30,
            bgcolor: 'rgba(8, 10, 14, 0.84)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 300,
              px: 3,
              pt: 3,
              pb: 2.25,
              borderRadius: 3,
              border: '1px solid rgba(76, 154, 255, 0.5)',
              bgcolor: 'rgba(14, 20, 30, 0.98)',
              textAlign: 'center',
              boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }} aria-hidden>
              <svg width="72" height="44" viewBox="0 0 72 44">
                <line x1="14" y1="32" x2="36" y2="8" stroke="#4C9AFF" strokeWidth="1.6" />
                <line x1="36" y1="8" x2="58" y2="24" stroke="#4C9AFF" strokeWidth="1.6" />
                <line x1="14" y1="32" x2="58" y2="24" stroke="#4C9AFF" strokeWidth="1.6" />
                <circle cx="14" cy="32" r="4.5" fill="#4C9AFF" />
                <circle cx="36" cy="8" r="4.5" fill="#4C9AFF" />
                <circle cx="58" cy="24" r="4.5" fill="#4C9AFF" />
              </svg>
            </Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>
              Building your graph
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', mt: 0.5, mb: 2 }}>
              {GRAPH_BUILD_STEPS[buildStep]}
            </Typography>
            <LinearProgress
              sx={{
                height: 3,
                borderRadius: 1,
                bgcolor: 'rgba(76, 154, 255, 0.18)',
                '& .MuiLinearProgress-bar': { bgcolor: '#4C9AFF' },
              }}
            />
          </Box>
        </Box>
      )}
      {/* Compact Filter Controls - Icon Buttons with Popup Dialogs */}
      <Box sx={{ flexShrink: 0, mb: 0.5, maxHeight: '120px', overflowY: 'auto' }}>
        <Paper elevation={2} sx={{ px: 1, py: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {/* Filter Group */}
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Tooltip title="Date Range">
                <IconButton 
                  onClick={() => setDateDialogOpen(true)}
                  size="small"
                  sx={{ 
                    bgcolor: (dateRange.start || dateRange.end) ? 'primary.main' : 'action.hover',
                    color: (dateRange.start || dateRange.end) ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: 'primary.dark', color: 'white' },
                    width: 28,
                    height: 28,
                  }}
                >
                  <CalendarMonthIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Entities">
                <IconButton 
                  onClick={() => setEntityDialogOpen(true)}
                  size="small"
                  sx={{ 
                    bgcolor: (selectedEntityIds.length > 0 || selectedEntityTypes.length !== availableEntityTypes.length) ? 'secondary.main' : 'action.hover',
                    color: (selectedEntityIds.length > 0 || selectedEntityTypes.length !== availableEntityTypes.length) ? 'secondary.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: 'secondary.dark', color: 'white' },
                    width: 28,
                    height: 28,
                  }}
                >
                  <DevicesIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Filters">
                <IconButton 
                  onClick={() => setFilterDialogOpen(true)}
                  size="small"
                  sx={{ 
                    bgcolor: (connectionType !== 'all' || selectedLocations.length > 0 || selectedLocationCategories.length > 0) ? 'warning.main' : 'action.hover',
                    color: (connectionType !== 'all' || selectedLocations.length > 0 || selectedLocationCategories.length > 0) ? 'warning.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: 'warning.dark', color: 'white' },
                    width: 28,
                    height: 28,
                  }}
                >
                  <FilterListIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, my: 'auto' }} />

            {/* Connection Range Slider */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 280 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.65rem', minWidth: 45 }}>
                Min: {connectionRange[0]}
              </Typography>
              <Tooltip title={`Optimal: ${suggestedRange[0]}+`}>
                <span>
                  <IconButton 
                    onClick={() => setConnectionRange(suggestedRange)} 
                    size="small"
                    color={connectionRange[0] === suggestedRange[0] && suggestedRange[0] > 1 ? 'warning' : 'default'}
                    disabled={suggestedRange[0] === 1}
                    sx={{ p: 0.25 }}
                  >
                    <TuneIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Slider
                value={connectionRange}
                onChange={(_, value) => setConnectionRange(value as [number, number])}
                min={1}
                max={maxConnectionsInData}
                step={1}
                size="small"
                sx={{ width: '140px', mx: 1 }}
                disableSwap
              />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.65rem', minWidth: 55 }}>
                Max: {connectionRange[1]}
              </Typography>
              <Button 
                onClick={() => setConnectionRange([1, maxConnectionsInData])} 
                variant={connectionRange[0] === 1 && connectionRange[1] === maxConnectionsInData ? 'contained' : 'text'}
                size="small"
                sx={{ minWidth: 24, px: 0.5, py: 0, fontSize: '0.6rem', height: 18 }}
              >
                All
              </Button>
              {(connectionRange[0] > 1 || connectionRange[1] < maxConnectionsInData) && (
                <Chip 
                  label={`${currentTab.nodes.length - filteredData.nodes.length} hid`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }}
                />
              )}
            </Box>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, my: 'auto' }} />

            {/* View Mode Toggle - Always visible */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mr: 0.5 }}>
                View:
              </Typography>
              <ToggleButtonGroup
                value={graphViewMode}
                exclusive
                onChange={(_, val) => val && setGraphViewMode(val)}
                size="small"
                sx={{ height: 22 }}
              >
                <ToggleButton value="network" sx={{ px: 0.75, py: 0, fontSize: '0.55rem', minWidth: 55 }}>
                  <Tooltip title="Force-directed network graph">
                    <span>Network</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="radial" sx={{ px: 0.75, py: 0, fontSize: '0.55rem', minWidth: 55 }}>
                  <Tooltip title={selectedEntityIds.length > 0 ? "Ego-centric radial layout" : "Discovery mode - find interesting entities"}>
                    <span>Radial</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, my: 'auto' }} />

            {/* Depth Filter (only show when entities are selected AND in radial view) */}
            {selectedEntityIds.length > 0 && graphViewMode === 'radial' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Connection depth from selected entities">
                    <AccountTreeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mr: 0.5 }}>
                    Depth:
                  </Typography>
                  <ToggleButtonGroup
                    value={depthLevel}
                    exclusive
                    onChange={(_, val) => setDepthLevel(val)}
                    size="small"
                    sx={{ height: 22 }}
                  >
                    <ToggleButton value="all" sx={{ px: 0.75, py: 0, fontSize: '0.6rem', minWidth: 28 }}>
                      <Tooltip title={`All levels (${depthData.depthCounts.reduce((a, b) => a + b, 0)} nodes)`}>
                        <span>All</span>
                      </Tooltip>
                    </ToggleButton>
                    {[1, 2, 3, 4].map(level => (
                      <ToggleButton 
                        key={level} 
                        value={level} 
                        sx={{ px: 0.5, py: 0, fontSize: '0.6rem', minWidth: 24 }}
                        disabled={depthData.depthCounts.slice(0, level + 1).reduce((a, b) => a + b, 0) === 0}
                      >
                        <Tooltip title={`Level ${level}: ${depthData.depthCounts.slice(0, level + 1).reduce((a, b) => a + b, 0)} nodes (${depthData.depthCounts[level]} at this depth)`}>
                          <span>{level}</span>
                        </Tooltip>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {depthLevel !== 'all' && (
                    <Chip 
                      label={`${depthFilteredData.nodes.length} nodes`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }}
                    />
                  )}
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, my: 'auto' }} />
              </>
            )}


            {/* Known Devices Toggle */}
            <ToggleButtonGroup
              value={showOnlyKnownDevices ? 'known' : 'all'}
              exclusive
              onChange={(_, val) => val && setShowOnlyKnownDevices(val === 'known')}
              size="small"
              sx={{ height: 22, flexShrink: 0 }}
            >
              <ToggleButton value="all" sx={{ px: 0.75, py: 0, fontSize: '0.55rem', minWidth: 36 }}>
                <Tooltip title="Show all nodes including external numbers">
                  <span>All</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="known" sx={{ px: 0.75, py: 0, fontSize: '0.55rem', minWidth: 50 }}>
                <Tooltip title="Show only known entities (hide external phone numbers)">
                  <span>Known</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                <CircularProgress size={10} />
              </Box>
            )}

            {/* Consolidated Filter Pill */}
            {(() => {
              const activeFilters: string[] = [];
              
              // Graph selection filter (e.g., "95 Selected" from box selection)
              if (graphSelectedCount > 0) {
                activeFilters.push(`${graphSelectedCount} selected`);
              }
              if (connectionType !== 'digital') {
                const typeLabels: Record<string, string> = { 'all': 'All', 'call': 'Calls', 'sms': 'SMS', 'browsing': 'Browse', 'app': 'Apps', 'dwell': 'Dwells' };
                activeFilters.push(`${typeLabels[connectionType] || connectionType}`);
              }
              if (selectedEntityIds.length > 0) {
                activeFilters.push(`${selectedEntityIds.length} entities`);
              }
              if (depthLevel !== 'all' && selectedEntityIds.length > 0) {
                activeFilters.push(`Depth <=${depthLevel}`);
              }
              if (expandedNodes.size > 0) {
                activeFilters.push(`${expandedNodes.size} expanded`);
              }
              if (hiddenNodes.size > 0) {
                activeFilters.push(`${hiddenNodes.size} hidden`);
              }
              if (selectedDates && selectedDates.size > 0) {
                activeFilters.push(`${selectedDates.size} days`);
              }
              if (selectedHours && selectedHours.length > 0) {
                activeFilters.push(`${selectedHours.length} hours`);
              }
              if (selectedConnectionTypes && selectedConnectionTypes.size > 0) {
                activeFilters.push(`${selectedConnectionTypes.size} types`);
              }
              if (selectedNodeTypes && selectedNodeTypes.size > 0) {
                activeFilters.push(`${selectedNodeTypes.size} nodes`);
              }
              if (selectedLocations.length > 0) {
                activeFilters.push(`${selectedLocations.length} locs`);
              }
              if (selectedArea) {
                activeFilters.push('Area selected');
              }
              
              if (activeFilters.length === 0) return null;
              
              const clearAllFilters = () => {
                graphRef.current?.clearSelection(); // Clear graph selection
                graphRef.current?.focusNode(null); // Clear click/entity focus highlight
                setAiHighlightedNodes(new Set()); // Clear AI/entity glow highlights
                setConnectionType('digital');
                setSelectedEntityIds([]);
                setSelectedEntityTypes(availableEntityTypes); // Reset to all types
                setDepthLevel('all');
                handleClearExpansions();
                setHiddenNodes(new Set());
                setSelectedDates(null);
                setSelectedHours(null);
                setSelectedConnectionTypes(null);
                setSelectedNodeTypes(null);
                setSelectedLocations([]);
                setSelectedLocationCategories([]); // Also reset location categories
                setSelectedArea(null);
              };
              
              return (
                <Tooltip title={`Click to clear filters: ${activeFilters.join(', ')}`}>
                  <Chip
                    icon={<FilterListIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label={`${activeFilters.length} Filter${activeFilters.length > 1 ? 's' : ''} Active | Click to Clear`}
                    size="small"
                    onClick={clearAllFilters}
                    aria-label={`Clear ${activeFilters.length} active filter${activeFilters.length > 1 ? 's' : ''}`}
                    sx={{ 
                      ml: 'auto',
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.16)' : 'rgba(25, 118, 210, 0.10)',
                      color: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#0d47a1',
                      border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.45)' : 'rgba(13, 71, 161, 0.30)'}`,
                      height: 26,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      letterSpacing: '0.01em',
                      '& .MuiChip-label': { px: 0.9 },
                      '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                      '&:hover': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.26)' : 'rgba(25, 118, 210, 0.18)',
                        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.75)' : 'rgba(13, 71, 161, 0.50)',
                      }
                    }}
                  />
                </Tooltip>
              );
            })()}

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />
          </Stack>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {loadNotice && !error && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {loadNotice}
          </Alert>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Paper elevation={0} sx={{ px: 1 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-flexContainer': {
                lineHeight: '12px',
                height: '28px',
                fontSize: '14px'
              }
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.label}
                    {!tab.isOriginal && (
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title="Rename">
                          <IconButton
                            component="span"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameTab(tab.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton
                            component="span"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateTab(tab.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                          <IconButton
                            component="span"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTab(tab.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>
      </Box>

      {/* Sentinel Panel + Network Graph - Takes remaining space */}
      <Box sx={{ 
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        position: 'relative',
      }}>
        {/* Collapsible Ask Sentinel Panel - Left Side */}
        <Box sx={{ 
          display: 'flex',
          flexShrink: 0,
          height: '100%',
          transition: 'width 0.3s ease',
          width: showSentinelPanel ? 300 : 0,
          overflow: 'hidden',
        }}>
          {showSentinelPanel && (
            <Box sx={{ 
              width: 300,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRight: 1,
              borderColor: 'divider',
            }}>
            <LinkAnalysisSentinel
              // ALWAYS use graph's filtered data when it's available (more than 0 nodes reported)
              // This ensures Sentinel sees the same data the user sees in the graph
              nodes={graphFilteredNodes.length > 0 ? graphFilteredNodes : graphDisplayData.nodes}
              links={graphFilteredLinks.length > 0 ? graphFilteredLinks : graphDisplayData.links}
              selectedEntityIds={selectedEntityIds}
              dateRange={dateRange}
              connectionType={connectionType}
              onNodeSelect={(nodeId) => {
                const node = graphDisplayData.nodes.find((n: any) => n.id === nodeId);
                if (node) {
                  handleNodeClick(node);
                }
              }}
              onGraphAction={handleGraphAction}
              onDateRangeChange={(start, end) => {
                setDateRange({ start, end });
                console.log(`[AI] Date range changed via callback: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`);
              }}
              // Additional context for AI - reflects current graph state
              selectedNodesCount={graphSelectedCount}
              flowMapEnabled={connectionType === 'dwell'}
              visibleLocationsCount={(graphFilteredNodes.length > 0 ? graphFilteredNodes : graphDisplayData.nodes).filter((n: any) => isLinkAnalysisPlaceNode(n)).length}
              // Tell Sentinel if the graph is showing a filtered view
              isFilteredView={graphSelectedCount > 0}
              totalNodesInDataset={graphDisplayData.nodes.length}
              totalLinksInDataset={graphDisplayData.links.length}
            />
            </Box>
          )}
        </Box>

        {/* Collapse/Expand Tab */}
        <Box
          onClick={() => setShowSentinelPanel(!showSentinelPanel)}
          sx={{
            position: 'absolute',
            left: showSentinelPanel ? 300 : 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 80,
            bgcolor: 'primary.main',
            borderRadius: showSentinelPanel ? '0 8px 8px 0' : '0 8px 8px 0',
            cursor: 'pointer',
            transition: 'left 0.3s ease, background-color 0.2s',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            boxShadow: 2,
          }}
        >
          <Tooltip title={showSentinelPanel ? "Hide Sentinel" : "Show Sentinel"} placement="right">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white' }}>
              {showSentinelPanel ? (
                <ChevronLeftIcon sx={{ fontSize: '1.2rem' }} />
              ) : (
                <>
                  <PsychologyIcon sx={{ fontSize: '1rem', mb: 0.5 }} />
                  <ChevronRightIcon sx={{ fontSize: '1rem' }} />
                </>
              )}
            </Box>
          </Tooltip>
        </Box>

        {/* Network Graph / Ego Radial Graph */}
        <Box sx={{ 
          flex: 1, 
          minWidth: 0,
          ml: showSentinelPanel ? 0 : 3, // Space for the tab when collapsed
          transition: 'margin-left 0.3s ease',
      }}>
        {graphViewMode === 'network' ? (
          <NetworkGraph
            ref={graphRef}
            nodes={graphDisplayData.nodes}
            links={graphDisplayData.links}
            originalNodes={originalTab.nodes}
            originalLinks={originalTab.links}
            width={0}
            height={0}
            onNodeClick={handleNodeClick}
            onSelectionChange={handleSelectionChange}
            selectionMode={selectionModeEnabled}
            onSelectionModeChange={setSelectionModeEnabled}
            appearanceSettings={linkAnalysisSettings}
            onAddDeviceNote={handleAddDeviceNote}
            onSaveAsGeofence={handleSaveAsGeofence}
            onAddLocationNote={handleAddLocationNote}
            onExportSubgraph={handleExportSubgraph}
            selectedArea={selectedArea}
            onAreaSelect={setSelectedArea}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleClearExpansions}
            onExportEntityReport={handleExportEntityReport}
            hiddenNodes={hiddenNodes}
            onToggleHide={handleToggleHide}
            selectedDates={selectedDates || undefined}
            onDateFilter={setSelectedDates}
            selectedHours={selectedHours || undefined}
            onHourFilter={setSelectedHours}
            selectedConnectionTypes={selectedConnectionTypes || undefined}
            connectionType={connectionType}
            onConnectionTypeFilter={setSelectedConnectionTypes}
            selectedNodeTypes={selectedNodeTypes || undefined}
            onNodeTypeFilter={setSelectedNodeTypes}
            onEntityFilter={setSelectedEntityIds}
            isSimulationFrozen={isSimulationFrozen}
            onSimulationFreezeChange={setIsSimulationFrozen}
            showOnlyKnownDevices={showOnlyKnownDevices}
            onShowOnlyKnownDevicesChange={setShowOnlyKnownDevices}
            aiHighlightedNodes={aiHighlightedNodes}
            sidePanelOpen={showRightPanel}
            onSidePanelToggle={setShowRightPanel}
            onViewDetails={handleViewDetails}
            onFindSimilar={handleFindSimilar}
            onFilteredDataChange={handleGraphFilteredDataChange}
          />
        ) : (
          <EgoRadialGraph
            nodes={depthFilteredData.nodes}
            links={depthFilteredData.links}
            selectedEntityIds={selectedEntityIds}
            nodeDepths={depthData.nodeDepths}
            depthLevel={depthLevel}
            onNodeClick={handleNodeClick}
            onToggleExpand={handleToggleExpand}
            onToggleHide={handleToggleHide}
            onEntityFilter={setSelectedEntityIds}
            onViewDetails={handleViewDetails}
            expandedNodes={expandedNodes}
          />
        )}
        </Box>
      </Box>

      {/* Dialogs moved out of the main layout flow to prevent duplication issues */}
      <Dialog 
        open={dateDialogOpen} 
        onClose={() => setDateDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        TransitionProps={{
          onEnter: () => {
            // Initialize local state with current values when dialog opens
            setLocalDateRange({ start: dateRange.start, end: dateRange.end });
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Select Date Range
          <IconButton size="small" onClick={() => setDateDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {(localDateRange.start || localDateRange.end) && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.main', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'warning.contrastText' }}>
                {localDateRange.start?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {localDateRange.end?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
              <IconButton size="small" onClick={() => setLocalDateRange({ start: null, end: null })} sx={{ color: 'warning.contrastText' }}><CloseIcon fontSize="small" /></IconButton>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Quick Select</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {[
              { label: 'Last 7 Days', days: 7 },
              { label: 'Last 30 Days', days: 30 },
              { label: 'Last 90 Days', days: 90 },
            ].map((option) => {
              // Check if this option is currently selected
              const isSelected = (() => {
                if (!localDateRange.start || !localDateRange.end) return false;
                const diffInMs = localDateRange.end.getTime() - localDateRange.start.getTime();
                const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
                return diffInDays === option.days;
              })();
              
              return (
                <Button
                  key={option.label}
                  variant={isSelected ? "contained" : "outlined"}
                  size="small"
                  color={isSelected ? "primary" : "inherit"}
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - option.days);
                    setLocalDateRange({ start, end });
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Stack>
          <TextField label="Start Date" type="date" fullWidth size="small" value={localDateRange.start ? localDateRange.start.toISOString().split('T')[0] : ''} onChange={(e) => { const newStart = e.target.value ? new Date(e.target.value) : null; setLocalDateRange(prev => ({ ...prev, start: newStart })); }} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField label="End Date" type="date" fullWidth size="small" value={localDateRange.end ? localDateRange.end.toISOString().split('T')[0] : ''} onChange={(e) => { const newEnd = e.target.value ? new Date(e.target.value) : null; setLocalDateRange(prev => ({ ...prev, end: newEnd })); }} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocalDateRange({ start: null, end: null })}>Clear</Button>
          <Button variant="contained" onClick={() => { setDateRange(localDateRange); setDateDialogOpen(false); }}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={entityDialogOpen} 
        onClose={() => setEntityDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        TransitionProps={{
          onEnter: () => {
            // Initialize local state with current values when dialog opens
            setLocalEntityTypes(selectedEntityTypes);
            setLocalEntityIds(selectedEntityIds);
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Entity Filters
          <IconButton size="small" onClick={() => setEntityDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2.5, mt: 1 }}>
            <InputLabel shrink>Entity Type</InputLabel>
            <Select multiple value={localEntityTypes} label="Entity Type" onChange={(e) => { 
              const value = e.target.value as string[]; 
              let nextTypes: string[];
              if (value.includes('All')) { 
                // Clicked "All" - toggle: if all selected, clear; otherwise select all
                nextTypes = localEntityTypes.length === availableEntityTypes.length ? [] : availableEntityTypes;
              } else if (localEntityTypes.length === availableEntityTypes.length && value.length === availableEntityTypes.length - 1) {
                // Was "All" (all types selected), user clicked a specific type - select ONLY that type
                const clickedType = availableEntityTypes.find(t => !value.includes(t));
                nextTypes = clickedType ? [clickedType] : value;
              } else { 
                nextTypes = value;
              } 
              setLocalEntityTypes(nextTypes);
              // Cascade: drop any selected entity IDs that no longer belong to the selected types.
              // When nextTypes is empty or covers all types, keep existing selections as-is.
              if (nextTypes.length > 0 && nextTypes.length < availableEntityTypes.length) {
                const allowed = new Set(nextTypes);
                setLocalEntityIds((prev) => prev.filter((id) => {
                  const t = deviceEntityTypeMap.get(id);
                  return !t || allowed.has(t);
                }));
              }
            }} disabled={availableEntityTypes.length === 0} displayEmpty notched renderValue={(selected) => selected.length === 0 || selected.length === availableEntityTypes.length ? 'All' : selected.length === 1 ? selected[0] : `${selected.length} types`}>
              <MenuItem key="All" value="All" dense><Checkbox checked={localEntityTypes.length === availableEntityTypes.length} indeterminate={localEntityTypes.length > 0 && localEntityTypes.length < availableEntityTypes.length} size="small" /><ListItemText primary="All" primaryTypographyProps={{ fontWeight: 'bold' }} /></MenuItem>
              {availableEntityTypes.map((type) => (<MenuItem key={type} value={type} dense><Checkbox checked={localEntityTypes.indexOf(type) > -1} size="small" /><ListItemText primary={type} /></MenuItem>))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel shrink>Filter by Entity</InputLabel>
            <Select 
              multiple 
              value={localEntityIds} 
              label="Filter by Entity" 
              onChange={(e) => setLocalEntityIds(e.target.value as string[])} 
              disabled={availableEntityIds.length === 0} 
              displayEmpty 
              notched 
              renderValue={(selected) => selected.length === 0 ? 'All Entities' : selected.length === 1 ? selected[0] : `${selected.length} entities`} 
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } }, autoFocus: false }}
              onClose={() => setEntitySearch('')}
            >
              <ListSubheader sx={{ bgcolor: 'background.paper', pt: 1, pb: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search entities..."
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
              </ListSubheader>
              {(() => {
                // Cascade entity list from currently-selected Entity Types.
                // Treat "no types selected" and "all types selected" the same as no type filter.
                const typeFilterActive =
                  localEntityTypes.length > 0 && localEntityTypes.length < availableEntityTypes.length;
                const allowedTypes = new Set(localEntityTypes);
                const visibleIds = availableEntityIds.filter((id) => {
                  if (typeFilterActive) {
                    const t = deviceEntityTypeMap.get(id);
                    if (!t || !allowedTypes.has(t)) return false;
                  }
                  if (entitySearch && !id.toLowerCase().includes(entitySearch.toLowerCase())) return false;
                  return true;
                });
                if (visibleIds.length === 0) {
                  return (
                    <MenuItem disabled dense>
                      <ListItemText primary={typeFilterActive ? 'No entities for selected entity type(s)' : 'No matches found'} />
                    </MenuItem>
                  );
                }
                return visibleIds.map((entityId) => (
                  <MenuItem key={entityId} value={entityId} dense>
                    <Checkbox checked={localEntityIds.indexOf(entityId) > -1} size="small" />
                    <ListItemText primary={entityId} />
                  </MenuItem>
                ));
              })()}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { 
            // Reset to all available types (or empty if none available)
            setLocalEntityTypes(availableEntityTypes.length > 0 ? availableEntityTypes : []); 
            setLocalEntityIds([]); 
          }}>Clear</Button>
          <Button variant="contained" onClick={() => { 
            setSelectedEntityTypes(localEntityTypes.length > 0 ? localEntityTypes : availableEntityTypes); 
            setSelectedEntityIds(localEntityIds); 
            setEntityDialogOpen(false); 
            // Single-entity filters can use node focus. Multi-entity filters
            // should not focus the first entity, because focus fades the rest.
            if (localEntityIds.length > 0) {
              setAiHighlightedNodes(new Set(localEntityIds));
              if (localEntityIds.length === 1) {
                // Defer to let filteredData + layout settle before zooming.
                setTimeout(() => {
                  graphRef.current?.focusNode(localEntityIds[0]);
                }, 120);
              } else {
                graphRef.current?.focusNode(null);
              }
            } else {
              // No entity filter -> clear focus so the graph returns to normal.
              setAiHighlightedNodes(new Set());
              graphRef.current?.focusNode(null);
            }
          }}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        TransitionProps={{
          onEnter: () => {
            // Initialize local state with current values when dialog opens
            setLocalConnectionType(connectionType);
            setLocalLocations(selectedLocations);
            setLocalCategories(selectedLocationCategories);
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Connection & Location Filters
          <IconButton size="small" onClick={() => setFilterDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2.5, mt: 1 }}>
            <InputLabel shrink>Connection Type</InputLabel>
            <Select value={localConnectionType} label="Connection Type" onChange={(e) => setLocalConnectionType(e.target.value)} displayEmpty notched>
              {CONNECTION_TYPES.map((opt) => (<MenuItem key={opt.value} value={opt.value} dense>{opt.label}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2.5, mt: 1 }}>
            <InputLabel shrink>Filter by Location</InputLabel>
            <Select 
              multiple 
              value={localLocations} 
              label="Filter by Location" 
              onChange={(e) => setLocalLocations(e.target.value as string[])} 
              disabled={availableLocations.length === 0} 
              displayEmpty 
              notched 
              renderValue={(selected) => selected.length === 0 ? 'All Locations' : selected.length === 1 ? availableLocations.find(loc => loc.id === selected[0])?.label || selected[0] : `${selected.length} locations`} 
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } }, autoFocus: false }}
              onClose={() => setLocationSearch('')}
            >
              {availableLocations.length === 0 && (
                <MenuItem disabled dense><ListItemText primary={localConnectionType !== connectionType ? 'Apply connection type first, then reopen to filter' : 'No locations in current data'} /></MenuItem>
              )}
              <ListSubheader sx={{ bgcolor: 'background.paper', pt: 1, pb: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search locations..."
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
              </ListSubheader>
              {availableLocations
                .filter(loc => !locationSearch || loc.label.toLowerCase().includes(locationSearch.toLowerCase()))
                .map((location) => (
                  <MenuItem key={location.id} value={location.id} dense>
                    <Checkbox checked={localLocations.indexOf(location.id) > -1} size="small" />
                    <ListItemText primary={location.label} />
                  </MenuItem>
                ))}
              {availableLocations.filter(loc => !locationSearch || loc.label.toLowerCase().includes(locationSearch.toLowerCase())).length === 0 && (
                <MenuItem disabled dense><ListItemText primary="No matches found" /></MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel shrink>Filter by Category</InputLabel>
            <Select 
              multiple 
              value={localCategories} 
              label="Filter by Category" 
              onChange={(e) => setLocalCategories(e.target.value as string[])} 
              disabled={availableLocationCategories.length === 0} 
              displayEmpty 
              notched 
              renderValue={(selected) => selected.length === 0 ? 'All Categories' : selected.length === 1 ? selected[0] : `${selected.length} categories`} 
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } }, autoFocus: false }}
              onClose={() => setCategorySearch('')}
            >
              <ListSubheader sx={{ bgcolor: 'background.paper', pt: 1, pb: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
              </ListSubheader>
              {availableLocationCategories
                .filter(cat => !categorySearch || cat.toLowerCase().includes(categorySearch.toLowerCase()))
                .map((category) => (
                  <MenuItem key={category} value={category} dense>
                    <Checkbox checked={localCategories.indexOf(category) > -1} size="small" />
                    <ListItemText primary={category} />
                  </MenuItem>
                ))}
              {availableLocationCategories.filter(cat => !categorySearch || cat.toLowerCase().includes(categorySearch.toLowerCase())).length === 0 && (
                <MenuItem disabled dense><ListItemText primary={localConnectionType !== connectionType ? 'Apply connection type first, then reopen to filter' : 'No categories in current data'} /></MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLocalConnectionType('digital'); setLocalLocations([]); setLocalCategories([]); }}>Clear</Button>
          <Button variant="contained" onClick={() => {
            const typeChanging = localConnectionType !== connectionType;
            setConnectionType(localConnectionType);
            if (typeChanging && (localLocations.length > 0 || localCategories.length > 0)) {
              // Stash for after the fetch completes — current available* lists
              // are from the old connection type and will be stale.
              pendingLocFiltersRef.current = { locations: localLocations, categories: localCategories };
              setSelectedLocations([]);
              setSelectedLocationCategories([]);
            } else {
              setSelectedLocations(localLocations);
              setSelectedLocationCategories(localCategories);
            }
            setFilterDialogOpen(false);
          }}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Tab</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Tab Name" type="text" fullWidth value={renameTabLabel} onChange={(e) => setRenameTabLabel(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') { handleRenameConfirm(); } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameConfirm} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      {noteDialogTarget && (
        <NoteDialog
          open={noteDialogOpen}
          onClose={() => {
            setNoteDialogOpen(false);
            setNoteDialogTarget(null);
          }}
          entityType={noteDialogTarget.entityType}
          entityId={noteDialogTarget.entityId}
          entityLabel={noteDialogTarget.entityLabel}
          latitude={noteDialogTarget.latitude}
          longitude={noteDialogTarget.longitude}
          onSave={(note: Note) => {
            console.log('Note saved:', note);
            // Could add a snackbar notification here
          }}
        />
      )}

      {/* Entity Details Dialog */}
      {detailsDialogTarget && (
        <EntityDetailsDialog
          open={detailsDialogOpen}
          onClose={() => {
            setDetailsDialogOpen(false);
            setDetailsDialogTarget(null);
          }}
          entityType={detailsDialogTarget.entityType}
          entityId={detailsDialogTarget.entityId}
          entityLabel={detailsDialogTarget.entityLabel}
          stats={detailsDialogTarget.stats}
          websiteDetails={detailsDialogTarget.websiteDetails}
          dateRange={dateRange}
          onAddNote={() => {
            // Open note dialog for this entity
            setNoteDialogTarget({
              entityType: detailsDialogTarget.entityType,
              entityId: detailsDialogTarget.entityId,
              entityLabel: detailsDialogTarget.entityLabel,
            });
            setNoteDialogOpen(true);
          }}
        />
      )}

      {/* Similar Entities Floating Panel (for embedding-based similarity search) */}
      {showSimilarPanel && similarPanelEntity && selectedCourse && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            right: showRightPanel ? 340 : 16,
            top: 120,
            width: 340,
            maxHeight: 'calc(100vh - 200px)',
            zIndex: 1200,
            overflow: 'hidden',
            borderRadius: 2,
            transition: 'right 0.3s ease',
          }}
        >
          <Box sx={{ 
            p: 1, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Similar Behavioral Profiles
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setShowSimilarPanel(false)}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {/* Course Selector */}
          {availableCourses.length > 1 && (
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
              <FormControl fullWidth size="small">
                <InputLabel>{courseSingular}</InputLabel>
                <Select
                  value={selectedCourse}
                  label={courseSingular}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                >
                  {availableCourses.map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          
          <SimilarEntitiesPanel
            selectedEntityId={similarPanelEntity}
            courseId={selectedCourse}
            onEntitySelect={(entityId: string) => {
              // Highlight the entity in the graph
              setSelectedEntityIds([entityId]);
              setSimilarPanelEntity(entityId);
            }}
            onEntityHighlight={(entityIds: string[]) => {
              setSelectedEntityIds(entityIds);
            }}
            maxResults={10}
            minSimilarity={0.3}
            compact={true}
          />
        </Paper>
      )}
    </Box>
  );
};

export default LinkAnalysisPage;
